import { useEffect } from 'react';
import './FlameBurstAnimation.css';

export default function FlameBurstAnimation({ playerRef, opponentRef, onComplete }) {
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    const battleBg = playerEl.closest('.battle-bg') || document.querySelector('.battle-bg');
    if (!battleBg) {
      console.error('Battle background not found');
      onComplete();
      return;
    }

    const pRect = playerEl.getBoundingClientRect();
    const oRect = opponentEl.getBoundingClientRect();
    const bRect = battleBg.getBoundingClientRect();

    const startX = (pRect.left + pRect.width / 2) - bRect.left;
    const startY = (pRect.top + pRect.height / 2) - bRect.top;
    const endX = (oRect.left + oRect.width / 2) - bRect.left;
    const endY = (oRect.top + oRect.height / 2) - bRect.top;

    const elements = [];
    const timers = [];

    // Projectile orb
    const orb = document.createElement('div');
    orb.className = 'flameburst-orb';
    orb.style.left = `${startX}px`;
    orb.style.top = `${startY}px`;
    orb.style.setProperty('--dx', `${endX - startX}px`);
    orb.style.setProperty('--dy', `${endY - startY}px`);
    battleBg.appendChild(orb);
    elements.push(orb);

    // Pre-flare at muzzle
    const pre = document.createElement('div');
    pre.className = 'flameburst-preflare';
    pre.style.left = `${startX}px`;
    pre.style.top = `${startY}px`;
    battleBg.appendChild(pre);
    elements.push(pre);
    const preRm = setTimeout(() => pre.remove(), 220);
    timers.push(preRm);

    // Comet trails along path
    const angleRad = Math.atan2(endY - startY, endX - startX);
    for (let i = 0; i < 4; i++) {
      const tr = document.createElement('div');
      tr.className = 'flameburst-trail';
      tr.style.left = `${startX}px`;
      tr.style.top = `${startY}px`;
      const jitter = (Math.random() - 0.5) * 10; // degrees
      tr.style.setProperty('--rot', `${(angleRad * 180 / Math.PI) + jitter}deg`);
      tr.style.setProperty('--dx', `${endX - startX}px`);
      tr.style.setProperty('--dy', `${endY - startY}px`);
      battleBg.appendChild(tr);
      elements.push(tr);
      const trRm = setTimeout(() => tr.remove(), 380);
      timers.push(trRm);
    }

    const explode = () => {
      // Clean orb
      if (orb.isConnected) orb.remove();

      // Opponent reaction
      opponentEl.classList.add('flameburst-hit');
      const rmHit = setTimeout(() => opponentEl.classList.remove('flameburst-hit'), 320);
      timers.push(rmHit);

      // Camera shake
      battleBg.classList.add('flameburst-shake');
      const shakeRm = setTimeout(() => battleBg.classList.remove('flameburst-shake'), 280);
      timers.push(shakeRm);

      // Flash
      const flash = document.createElement('div');
      flash.className = 'flameburst-flash';
      flash.style.left = `${endX}px`;
      flash.style.top = `${endY}px`;
      battleBg.appendChild(flash);
      elements.push(flash);
      const flashRm = setTimeout(() => flash.remove(), 200);
      timers.push(flashRm);

      // Core flash
      const core = document.createElement('div');
      core.className = 'flameburst-core';
      core.style.left = `${endX}px`;
      core.style.top = `${endY}px`;
      battleBg.appendChild(core);
      elements.push(core);
      const coreRm = setTimeout(() => core.remove(), 220);
      timers.push(coreRm);

      // Shockwave ring
      const ring = document.createElement('div');
      ring.className = 'flameburst-shockwave';
      ring.style.left = `${endX}px`;
      ring.style.top = `${endY}px`;
      battleBg.appendChild(ring);
      elements.push(ring);
      const ringRm = setTimeout(() => ring.remove(), 600);
      timers.push(ringRm);

      // Heat haze ring
      const heat = document.createElement('div');
      heat.className = 'flameburst-heatwave';
      heat.style.left = `${endX}px`;
      heat.style.top = `${endY}px`;
      battleBg.appendChild(heat);
      elements.push(heat);
      const heatRm = setTimeout(() => heat.remove(), 700);
      timers.push(heatRm);

      // Burst embers
      for (let i = 0; i < 32; i++) {
        const ember = document.createElement('div');
        ember.className = 'flameburst-ember';
        ember.style.left = `${endX}px`;
        ember.style.top = `${endY}px`;
        const ang = Math.random() * Math.PI * 2;
        const dist = 22 + Math.random() * 42;
        ember.style.setProperty('--dx', `${Math.cos(ang) * dist}px`);
        ember.style.setProperty('--dy', `${Math.sin(ang) * dist}px`);
        ember.style.setProperty('--scale', `${0.7 + Math.random() * 0.7}`);
        battleBg.appendChild(ember);
        elements.push(ember);
        const emRm = setTimeout(() => ember.remove(), 650 + Math.random() * 250);
        timers.push(emRm);
      }

      // Larger flame chunks
      for (let i = 0; i < 8; i++) {
        const f = document.createElement('div');
        f.className = 'flameburst-chunk';
        f.style.left = `${endX}px`;
        f.style.top = `${endY}px`;
        const ang = Math.random() * Math.PI * 2;
        const dist = 26 + Math.random() * 50;
        f.style.setProperty('--dx', `${Math.cos(ang) * dist}px`);
        f.style.setProperty('--dy', `${Math.sin(ang) * dist}px`);
        f.style.setProperty('--rot', `${(Math.random() * 90 - 45)}deg`);
        battleBg.appendChild(f);
        elements.push(f);
        const fRm = setTimeout(() => f.remove(), 700 + Math.random() * 250);
        timers.push(fRm);
      }

      // Finish
      const done = setTimeout(() => {
        timers.forEach(clearTimeout);
        elements.forEach(el => el.isConnected && el.remove());
        opponentEl.classList.remove('flameburst-hit');
        onComplete();
      }, 900);
      timers.push(done);
    };

    const onOrbEnd = () => {
      orb.removeEventListener('animationend', onOrbEnd);
      explode();
    };

    orb.addEventListener('animationend', onOrbEnd);

    // Safety cleanup
    const cleanupTimer = setTimeout(() => {
      orb.removeEventListener('animationend', onOrbEnd);
      elements.forEach(el => el.isConnected && el.remove());
      opponentEl.classList.remove('flameburst-hit');
      onComplete();
    }, 2200);
    timers.push(cleanupTimer);

    return () => {
      orb.removeEventListener('animationend', onOrbEnd);
      timers.forEach(clearTimeout);
      elements.forEach(el => el.isConnected && el.remove());
      opponentEl.classList.remove('flameburst-hit');
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

FlameBurstAnimation.moveType = 'fire';
